import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';

/**
 * Provides a wrapper over the Angular HttpClient that allows cached results.
 * Currently, the only way to invalidate the cache is to send an optional
 * `reset` value of `true`. Only GET requests are supported.
 *
 * Note: Any HttpInterceptors in use will still run against the underlying
 * HttpClient requests.
 */
@Injectable({ providedIn: 'root' })
export class CachedHttpService {
    // TODO: We may want to create a type to hold the cached data and some
    //  additional details like a time stamp so we don't let the cache go stale.
    private cache = new Map<string, CacheEntry<any>>();
    // 2 minutes in milliseconds.
    private timeout = 60 * 2 * 1000;

    constructor(private http: HttpClient) {}

    /**
     * Returns the cached response for the given url if exists and a reset is
     * not requested; otherwise, gets the results from a new http GET request.
     * @param url the url of the api endpoint
     * @param reset flag to reset and refresh the cache for the given url
     */
    get<T>(url: string, reset: boolean = false): Observable<T> {
        if (reset || this.shouldGet(url)) {
            // TODO: Verify we really need to delete it. For example, maybe
            //  on error we check if there's some data cached and send that back??
            // this.cache.delete(url);
            return this.http.get<T>(url).pipe(
                tap((t) => this.setCacheEntry(url, t)),
                // TODO: catchError? Maybe not our problem since we're just a wrapper.
            );
        }
        const data = this.cache.get(url)?.data;
        return of(data as T);
    }

    /**
     * Returns the cached response for the given url/key if exists and a reset is
     * not requested; otherwise, gets the results from a new http POST request.
     *
     * WARNING: Be careful caching POST requests as they typically make changes on the server/database
     * and may not be logically cached.
     * @param url the url of the api endpoint
     * @param body the POST body
     * @param key the key to the cache map (defaults to the url param value)
     * @param reset flag to reset and refresh the cache for the given url
     */
    post<R>(
        url: string,
        body: any,
        key: string = url,
        reset: boolean = false,
    ): Observable<R> {
        if (reset || this.shouldGet(key)) {
            return this.http
                .post<R>(url, body)
                .pipe(tap((r) => this.setCacheEntry(key, r)));
        }
        const data = this.cache.get(key)?.data;
        return of(data as R);
    }

    private shouldGet(key: string) {
        if (!this.cache.has(key)) return true;

        const now = new Date();
        const entry = this.cache.get(key)!;
        return +now - +entry.loaded >= this.timeout;
    }

    private setCacheEntry<Data>(key: string, data: Data) {
        const entry: CacheEntry<Data> = {
            data: data,
            loaded: new Date(),
        };
        this.cache.set(key, entry);
    }
}

type CacheEntry<R> = {
    data: R;
    loaded: Date;
};
